import React, { useState, useEffect, useRef } from 'react'
import Tooltip from 'britive-design-system/core/components/tooltip'
import Typography from 'britive-design-system/core/components/typography'
import MenuOptions from 'britive-design-system/core/utils/common-portal/menu-options-container'
import Terminal from 'static/images/terminal-access.svg'
import TerminalActive from 'static/images/terminal-access-active.svg'
import TerminalIndeterminate from 'static/images/terminal-access-indeterminate.svg'
import ExternalLinkIndeterminate from 'static/images/external-link-indeterminate.svg'
import ExternalLinkActive from 'static/images/external-link-active.svg'
import ExternalLink from 'static/images/external-link.svg'
import { formattedFromNowShort, formattedFromNow } from 'utils/format_date'
import { isProfileAccessIndeterminate, shouldDisableProfileAccess } from '../utils'
import { isEmpty } from 'lodash'

const CSS_CLASSNAME_PREFIX = 'my-resources'

const AccessCell = ({
  row,
  onProfileAccess,
  availableTemplates,
  accessProfileCreds,
}) => {
  const [showOptions, setShowOptions] = useState(false)
  const [containerDimension, setContainerDimension] = useState(null)
  const iconRef = useRef()
  const [remainingTime, setRemainingTime] = useState({
    short: '',
    full: '',
  })

  const isDisabled = shouldDisableProfileAccess(row.status)
  const isIndeterminate = isProfileAccessIndeterminate(row.status)
  const isConsoleEnabled = !isDisabled && !isIndeterminate && row.consoleAccess
  const isProgrammaticEnabled =
    !isDisabled && !isIndeterminate && (row.progAccess || row.showOrigCreds)

  const getContainerDimension = () => {
    let rect = iconRef.current.getBoundingClientRect()
    let dimension = {
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left,
      height: rect.height,
    }
    setContainerDimension(dimension)
  }

  useEffect(() => {
    let interval

    if (!isDisabled) {
      updateTimeRemaining()
      interval = setInterval(updateTimeRemaining, 30 * 1000)
    }

    return () => {
      clearInterval(interval)
    }
  }, [JSON.stringify(row), isDisabled])

  const updateTimeRemaining = () => {
    setRemainingTime({
      short: formattedFromNowShort(row.expirationDuration),
      full: formattedFromNow(row.expirationDuration),
    })
  }

  const onTemplateClick = (_, templateValue) => {
    accessProfileCreds(
      row?.transactionId,
      row.profileId,
      row.resourceId,
      true,
      templateValue.title
    )
    setShowOptions(false)
  }

  return (
    <div className={`${CSS_CLASSNAME_PREFIX}-column-access-icon`}>
      <div
        ref={iconRef}
        className={`${CSS_CLASSNAME_PREFIX}-column-access-icon${
          isConsoleEnabled ? '-enabled' : '-disabled'
        }`}
        onClick={() => {
          if (!isConsoleEnabled) {
            return
          }

          onProfileAccess(row?.transactionId, row.profileId, row.resourceId, true)
          getContainerDimension()
          setShowOptions(true)
        }}
      >
        <img
          src={
            isIndeterminate
              ? ExternalLinkIndeterminate
              : isConsoleEnabled
              ? ExternalLinkActive
              : ExternalLink
          }
        />
        {isConsoleEnabled && (
          <Tooltip title={`${remainingTime.full} remaining`} position="top">
            <div
              className={`${CSS_CLASSNAME_PREFIX}-column-access-icon-time-remaining`}
            >
              <Typography variant="helper1">{remainingTime.short}</Typography>
            </div>
          </Tooltip>
        )}
      </div>
      <div
        className={`${CSS_CLASSNAME_PREFIX}-column-access-icon${
          isProgrammaticEnabled ? '-enabled' : '-disabled'
        }`}
        onClick={() => {
          if (!isProgrammaticEnabled) {
            return
          }

          onProfileAccess(row?.transactionId, row.profileId, row.resourceId)
        }}
      >
        <img
          src={
            isIndeterminate
              ? TerminalIndeterminate
              : isProgrammaticEnabled
              ? TerminalActive
              : Terminal
          }
        />
        {isProgrammaticEnabled && (
          <Tooltip title={`${remainingTime.full} remaining`} position="top">
            <div
              className={`${CSS_CLASSNAME_PREFIX}-column-access-icon-time-remaining`}
            >
              <Typography variant="helper1">{remainingTime.short}</Typography>
            </div>
          </Tooltip>
        )}
      </div>
      {showOptions &&
        !availableTemplates.loading &&
        !isEmpty(availableTemplates?.data) &&
        availableTemplates.data?.length > 1 && (
          <MenuOptions
            portalContainerId={'overflow-menu-options'}
            options={availableTemplates?.data?.map(template => ({
              title: template,
            }))}
            getOptionLabel={option => option.title}
            containerDimension={containerDimension}
            onChange={onTemplateClick}
            width="300px"
          />
        )}
    </div>
  )
}

export default AccessCell
